/**
 * ===========================================================================
 *  NUSANTARA WARRIOR — THEME
 * ===========================================================================
 *
 * Who is fighting, what they are called, and what colour they are. One place.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 * ---------------------------------------------------------------------------
 * Faction colour used to live in seven places. Two of them were real sources
 * (the armour palettes in `chars/materials.js`, the banner palette here as
 * `world/layout.js` FACTION); the other five each carried their own private
 * copy of the same idea. Copies drift, and these already had:
 *
 *     ally   --ally #5da4ff  (ui/style.js)   vs  #6fb2ff  (ui/minimap.js)
 *     free   #cbbf9c         (ui/minimap.js) vs  #d6c6a4  (ui/select.js)
 *
 * Nobody chose that. It is what happens when a colour is retyped instead of
 * imported. Repainting the game for the Nusantara palette while five hidden
 * copies were still in play would have meant editing four files and hoping.
 *
 * ---------------------------------------------------------------------------
 * THREE SURFACES, NOT ONE COLOUR
 * ---------------------------------------------------------------------------
 * A faction is not one hex. It shows up on three surfaces with genuinely
 * different requirements, and the codebase was already right to keep them
 * apart — it just never said so:
 *
 *   banner   what flies over the forts and what the base architecture is
 *            painted with. Two flat, high-contrast colours read at 200 m.
 *            Consumed by `world/bases.js` and `ui/minimap.js`.
 *   armour   what five hundred men are wearing. Twenty-odd tuned colour roles
 *            per side, baked into a canvas atlas at boot. NOT held here — it
 *            stays in `chars/materials.js`, which this file points at through
 *            `materialKey`. That palette carries a hand-tuned exposure ramp;
 *            moving it is step three's problem, not step one's.
 *   ui       the HUD bars and labels. `main` is the flat colour, `hi`/`lo` the
 *            ends of a gradient fill, `label` the tinted caption. Read on a
 *            dark panel at 10 px, which is a different problem from cloth in
 *            sunlight, and legitimately a different value.
 *
 * ---------------------------------------------------------------------------
 * THIS FILE CHANGES NOTHING YET
 * ---------------------------------------------------------------------------
 * The centralisation step transcribed every value byte-for-byte, drifts and
 * all, so that a before/after capture could prove the rewiring changed nothing
 * (it did: max delta 0 across 13 frames, see tools/frame-diff.mjs). The drifts
 * were then settled here, in the repaint, where pixels are SUPPOSED to move:
 * ui and map now differ only by intent — the map runs a little brighter
 * because a 3 px marker on a baked relief needs more separation than a bar on
 * a dark panel does.
 */

/* ------------------------------------------------------------------ identity */

export const GAME = {
  name: 'Nusantara Warrior',
  subtitle: 'The Oath of a Thousand Isles',
};

/** Team indices, as used by `entities.team` and every consumer of it. */
export const SIDE = { PLAYER: 0, ENEMY: 1, NEUTRAL: 2 };

/* ------------------------------------------------------------------ helpers */

/** 0x2a5fbe -> '#2a5fbe' */
export const hexOf = (n) => '#' + (n >>> 0).toString(16).padStart(6, '0');

/** '#5da4ff' -> '93,164,255', for building rgba() strings in CSS and canvas. */
export function rgbOf(css) {
  const n = parseInt(String(css).replace('#', ''), 16) >>> 0;
  return ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255);
}

/* ----------------------------------------------------------------- factions */

/**
 * Order is significant: the array index IS the team number, so `FACTIONS[0]`
 * is the player's side. `materialKey` names this faction's armour palette in
 * `chars/materials.js`; the names there and here must stay in step.
 *
 * `name` is what prose calls the faction; `short` is what fits in a 196 px
 * minimap gutter. Both live here rather than in the HUD because the morale
 * bar, the stage-select legend and the mission log all name the same two
 * sides, and three hand-typed copies is how "Sui" survived the rename once
 * already.
 *
 * `key` and `materialKey` are deliberately NOT renamed with the factions.
 * They are the identifiers `chars/materials.js` and `crowd/atlas.js` bake
 * their armour atlases under at boot, and `select.js` names its legend
 * swatch classes after. Nothing renders them, so they are free to stay
 * `sui`/`kai`/`wei` until someone wants to pay for that refactor; what
 * matters is that no player-facing string is read from them.
 */
export const FACTIONS = [
  {
    key: 'sui',
    name: 'Wilwatikta',
    short: 'Wilwatikta',
    team: SIDE.PLAYER,
    materialKey: 'sui',
    // Gula kelapa: the red-and-white Majapahit banner the Merah Putih descends
    // from. `cloth` is the field, `trim` the white stripe — bases.js paints the
    // banner with cloth and the minimap outlines a marker with trim, so this is
    // where merah-putih actually reads.
    banner: { cloth: 0xc8352b, trim: 0xf2e7cf, accent: 0xe4bd72, dark: 0x6e1a14 },
    ui: { main: '#e0553f', hi: '#ff9a84', lo: '#8c2418', label: 'rgba(255,150,130,.85)' },
    map: { main: '#ef6a52', dim: 'rgba(214,86,64,.85)' },
  },
  {
    key: 'kai',
    name: 'Segara Kidul',
    // The morale bar sets two faction names 196 px apart at 7.5 u; the full
    // name and the enemy's together overrun it, and "Kidul" is what the host
    // is called in the field anyway.
    short: 'Kidul',
    team: SIDE.ENEMY,
    materialKey: 'kai',
    // Segara Kidul, off the water: deep indigo and pearl against the inland
    // red. Cold versus warm is what keeps the two sides separable at 44 px now
    // that the player is no longer the blue one.
    banner: { cloth: 0x1b3a63, trim: 0xc9d8e8, accent: 0x5fa8b8, dark: 0x0c1a2e },
    ui: { main: '#4fa8d8', hi: '#a8dcf5', lo: '#1b4a70', label: 'rgba(150,205,245,.85)' },
    map: { main: '#5fb4e0', dim: 'rgba(70,150,200,.85)' },
  },
  {
    key: 'free',
    name: 'Neutral',
    short: 'Neutral',
    team: SIDE.NEUTRAL,
    materialKey: 'wei',
    banner: { cloth: 0x3f6b46, trim: 0xd8d2bc, accent: 0x9fc8a4, dark: 0x1e3624 },
    ui: { main: '#d6c6a4', hi: '#efe4c8', lo: '#7d7256', label: 'rgba(214,198,164,.85)' },
    map: { main: '#cbbf9c', dim: 'rgba(203,191,156,.85)' },
  },
];

/** Never out of range: an unknown team falls back to neutral, as before. */
export function factionOf(team) {
  const n = FACTIONS.length;
  const i = ((team | 0) % n + n) % n;
  return FACTIONS[i] || FACTIONS[SIDE.NEUTRAL];
}

/** Shorthands the call sites actually want. */
export const uiColor = (team) => factionOf(team).ui.main;
export const mapColor = (team) => factionOf(team).map.main;
export const bannerColor = (team, role = 'cloth') => factionOf(team).banner[role];

/* ---------------------------------------------------------------- ornament */

/**
 * Decorative colours that are NOT faction-derived and must not become so.
 * `tassel` equalled the enemy's cloth red back when they were the red side,
 * which is exactly why it sat in `bases.js` as a bare `0xb0261f` and looked
 * like a faction colour that had escaped. It is the red tassel on a banner-
 * pole finial, the same on every pole whoever is flying the banner. Naming it
 * here keeps it out of the faction tables, where a future repaint would
 * wrongly capture it.
 */
export const ORNAMENT = {
  tassel: 0xb0261f,
  gold: '#f0d190',
};
